import { supabase } from "@/lib/supabase";

/**
 * Fire-and-forget client event sink → relay.client_events.
 *
 * Why this exists: browser-side query/mutation failures (e.g. a screen
 * querying a column that doesn't exist) previously had NO telemetry — the
 * only detection channel was a user manually reporting it. A global
 * QueryCache/MutationCache onError handler funnels every failure through
 * here so regressions are self-reporting.
 *
 * Contract:
 *  - NEVER throws and NEVER blocks the caller (returns void, work happens
 *    in a detached promise). A telemetry failure must not degrade UX.
 *  - Sanitized by construction: callers pass a short `name` + `message` +
 *    a small `detail` object. Do NOT pass raw request/response bodies,
 *    form values, tokens, or anything that may contain PII/secrets.
 *  - To record why an Error with a `cause` failed, put
 *    `describeErrorCause(error)` under `detail.cause`. Never serialize a cause
 *    object yourself: a PostgrestError's `details` and a FunctionsHttpError's
 *    `context` carry row values, headers, and response bodies.
 */
export interface ClientEvent {
  level?: "error" | "warn" | "info";
  source: "query" | "mutation" | "manual";
  /** Stable identifier — query/mutation key or an action name. */
  name: string;
  message?: string | null;
  detail?: Record<string, unknown>;
  /** Suppress browser path/user-agent context for privacy-sensitive events. */
  omitContext?: boolean;
}

const MAX_MESSAGE = 1000;
const MAX_NAME = 200;

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

/**
 * One link of an `Error.cause` chain as stored under `detail.cause`
 * (outermost cause first). Every field is derived through an allow-list; see
 * {@link describeErrorCause}.
 */
export interface ErrorCauseLink {
  /** Constructor-style name (`PostgrestError`, `TypeError`), else `"Error"`. */
  name: string;
  /** Redacted, whitespace-normalised, at most {@link MAX_CAUSE_MESSAGE} characters. */
  message?: string;
  /** SQLSTATE or PostgREST code (`42501`, `PGRST116`). */
  code?: string;
  /** HTTP status, when the cause object itself carries one. */
  status?: number;
}

const MAX_CAUSE_LINKS = 3;
const MAX_CAUSE_MESSAGE = 300;
const MAX_CAUSE_CHAIN_JSON = 1000;
// Bounds the text the redaction regexes ever scan (a failed fetch can put a
// whole HTML error page in `message`). Far above MAX_CAUSE_MESSAGE so a secret
// near the 300th character is still redacted whole instead of cut into a
// fragment too short to recognise.
const MAX_CAUSE_RAW = 2000;
const CAUSE_NAME = /^[A-Za-z][A-Za-z0-9]{0,39}$/;
const CAUSE_CODE = /^[A-Za-z0-9_]{1,16}$/;
const REDACTED = "[redacted]";

/** Cuts to `max` UTF-16 units without leaving half a surrogate pair (Postgres jsonb rejects a lone one). */
function sliceWellFormed(value: string, max: number): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

/**
 * Replaces each unpaired surrogate with U+FFFD. Postgres jsonb rejects the
 * whole document over one ("Unicode low surrogate must follow a high
 * surrogate"), which would lose the entire client event, outer message
 * included. Uses no lookbehind and no `toWellFormed` (ES2024, newer than this
 * project's lib target).
 */
function replaceLoneSurrogates(value: string): string {
  return value.replace(/[\ud800-\udbff][\udc00-\udfff]|[\ud800-\udfff]/g, (match) => (match.length === 2 ? match : "\ufffd"));
}

function redactSecrets(text: string): string {
  return (
    text
      // JWTs: header.payload.signature, each segment base64url.
      .replace(/\beyJ[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+){0,2}/g, REDACTED)
      // `Authorization: Bearer <token>` and bare `Bearer <token>`.
      .replace(/(\bBearer\s+)[A-Za-z0-9._~+/=-]+/gi, `$1${REDACTED}`)
      // key=value / key: value / "key":"value" for credential-looking keys.
      // The optional scheme word covers `authorization: Basic <credentials>`.
      .replace(
        /(api[_-]?key|authorization|token|secret|password|passwd)(["']?\s*[:=]\s*["']?)(?:(?:basic|bearer|digest|token)\s+)?[^\s"'&,;}]+/gi,
        `$1$2${REDACTED}`,
      )
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g, REDACTED)
      // Long opaque runs: hex ids, UUIDs, base64url keys. Only a run with a
      // digit or mixed case counts, so a long snake_case table or constraint
      // name (`reorder_rules_organization_id_fkey`), which is exactly what a
      // PostgREST message is useful for, stays readable.
      .replace(/[A-Za-z0-9_-]{24,}/g, (run) =>
        /\d/.test(run) || (/[a-z]/.test(run) && /[A-Z]/.test(run)) ? REDACTED : run,
      )
  );
}

function sanitizeCauseMessage(raw: string): string {
  // Control characters become spaces: JSON escapes each as six characters and
  // jsonb refuses NUL outright. A lone surrogate becomes U+FFFD for the same
  // two reasons. Redact BEFORE cutting to length so a secret straddling the
  // limit is removed whole.
  const normalised = replaceLoneSurrogates(sliceWellFormed(raw, MAX_CAUSE_RAW))
    .replace(/\p{Cc}+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  const redacted = redactSecrets(normalised);
  return redacted.length > MAX_CAUSE_MESSAGE
    ? `${sliceWellFormed(redacted, MAX_CAUSE_MESSAGE - 1)}…`
    : redacted;
}

function describeCauseLink(value: unknown): ErrorCauseLink {
  if (typeof value === "string") {
    const message = sanitizeCauseMessage(value);
    return message ? { name: "Error", message } : { name: "Error" };
  }
  if (typeof value !== "object" || value === null) return { name: "Error" };

  // Fixed property reads only, never the whole object. `details` (postgrest-js
  // documents it as "often the offending value, key, or row"), `hint`, `stack`,
  // and `context` (a FunctionsHttpError's raw Response: headers and body) are
  // deliberately not read. The `message` of an object is read only when it is
  // already a string, so an object never becomes "[object Object]".
  const source = value as { name?: unknown; message?: unknown; code?: unknown; status?: unknown };
  const link: ErrorCauseLink = {
    name: typeof source.name === "string" && CAUSE_NAME.test(source.name) ? source.name : "Error",
  };
  const message = typeof source.message === "string" ? sanitizeCauseMessage(source.message) : "";
  if (message) link.message = message;
  const code = source.code;
  if (typeof code === "string" && CAUSE_CODE.test(code)) link.code = code;
  const status = source.status;
  if (typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599) {
    link.status = status;
  }
  return link;
}

/**
 * Describes `error.cause` (not `error` itself: its message is already logged)
 * as a short chain safe to put in `detail.cause` of a client event, or
 * `undefined` when there is no cause. Intended for the global query/mutation
 * `onError` handlers, so a wrapped failure (an `Error` thrown with the
 * PostgREST or network error as its `cause`) keeps its real reason.
 *
 * Allow-list by construction: at most {@link MAX_CAUSE_LINKS} links, each with
 * only `name`, `message`, `code`, and `status`, every one validated or
 * redacted, and the serialised chain capped at {@link MAX_CAUSE_CHAIN_JSON}
 * characters by dropping trailing links. Never throws.
 *
 * Put the result in `detail`, never in `message` or `name`: the incident
 * pipeline derives signatures from those two, so changing them would split
 * existing incidents.
 */
export function describeErrorCause(error: unknown): ErrorCauseLink[] | undefined {
  const links: ErrorCauseLink[] = [];
  try {
    const readCause = (value: unknown): unknown =>
      typeof value === "object" && value !== null ? (value as { cause?: unknown }).cause : undefined;

    const seen = new Set<unknown>([error]);
    let current = readCause(error);
    while (current != null && links.length < MAX_CAUSE_LINKS) {
      if (typeof current === "object") {
        if (seen.has(current)) break;
        seen.add(current);
      }
      links.push(describeCauseLink(current));
      current = readCause(current);
    }
  } catch {
    // A hostile getter must not break telemetry: keep the links read so far.
  }

  // A single link always fits: its name (<= 40) and code (<= 16) are ASCII,
  // and its message is <= 300 characters with every control character and lone
  // surrogate already gone, so the worst case (300 quotes, which JSON doubles)
  // is about 720 serialized. Only whole trailing links ever need dropping.
  while (links.length > 1 && JSON.stringify(links).length > MAX_CAUSE_CHAIN_JSON) links.pop();
  return links.length > 0 ? links : undefined;
}

export function logClientEvent(evt: ClientEvent): void {
  // Detached — caller never awaits, errors are swallowed.
  void (async () => {
    try {
      let userId: string | null = null;
      try {
        const { data } = await supabase.auth.getUser();
        userId = data.user?.id ?? null;
      } catch {
        /* unauthenticated or auth unavailable — log anonymously */
      }

      const row = {
        app: "relay",
        level: evt.level ?? "error",
        source: evt.source,
        name: truncate(evt.name, MAX_NAME),
        message: evt.message
          ? truncate(String(evt.message), MAX_MESSAGE)
          : null,
        detail: evt.detail ?? {},
        user_id: userId,
        url:
          !evt.omitContext && typeof window !== "undefined"
            ? window.location.pathname
            : null,
        user_agent:
          !evt.omitContext && typeof navigator !== "undefined"
            ? truncate(navigator.userAgent, MAX_MESSAGE)
            : null,
      };

      // relay.client_events is intentionally NOT in the generated Database
      // types (added out-of-band as a telemetry sink; regenerating the 40k+
      // line database.ts for one internal table is not worth the churn/risk).
      // Narrow, localized cast keeps the rest of the app's supabase typing
      // fully intact while letting this one insert through.
      const sink = supabase.schema("relay").from("client_events" as never) as {
        insert: (values: Record<string, unknown>) => PromiseLike<unknown>;
      };
      await sink.insert(row);
    } catch (err) {
      // Last-resort: never throw out of telemetry. Surface in dev console.
      if (typeof console !== "undefined") {
        console.warn("[client-events] failed to record event", err);
      }
    }
  })();
}

const RELAY_ROUTE_GROUPS = new Set([
  "accept-invite",
  "accounts",
  "admin",
  "agency",
  "articles",
  "auth",
  "book",
  "booking",
  "bookings",
  "calendar",
  "calls",
  "companies",
  "contacts",
  "d",
  "dashboard",
  "data-deletion",
  "deals",
  "documents",
  "email-templates",
  "emails",
  "engagements",
  "f",
  "finance",
  "follow-ups",
  "forms",
  "inbox",
  "integrations",
  "jobs",
  "login",
  "notes",
  "orders",
  "portal",
  "privacy",
  "projects",
  "referrals",
  "relay",
  "reporting",
  "reset-password",
  "review",
  "reviews",
  "sequences",
  "settings",
  "sms",
  "sms-campaigns",
  "social",
  "tasks",
  "terms",
  "ugc",
  "unsubscribe",
]);

const REPORTING_CRASH_LOCATIONS = new Set([
  "reporting-social-overview",
  "reporting-crm-pipeline",
  "reporting-lead-sources",
  "reporting-bookings-revenue",
  "reporting-financials",
  "reporting-sms-sequences",
  "reporting-marketing-funnel",
]);

const SAFE_ERROR_NAMES = new Set([
  "Error",
  "EvalError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TypeError",
  "URIError",
]);

export function getRenderCrashRouteGroup(pathname?: string): string {
  const path =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const firstSegment = (
    path.split("/").filter(Boolean)[0] ?? "root"
  ).toLowerCase();
  return firstSegment === "root" || RELAY_ROUTE_GROUPS.has(firstSegment)
    ? firstSegment
    : "unknown";
}

/**
 * Records a render failure without arbitrary error text, URLs, stacks, or
 * user-agent data.
 *
 * Branches on session state:
 *  - Authenticated: writes directly to relay.client_events, gated by the
 *    pre-existing client_events_insert_self RLS policy (self-scoped,
 *    unrestricted content) — same as any other logClientEvent call.
 *  - No session: relay.client_events no longer grants anon INSERT (see
 *    20260812224500_relay_client_events_render_crash_rate_limit.sql —
 *    Codex flagged the prior anon grant as unbounded in request volume
 *    despite tightly bounded row content, PR #5210). The
 *    relay-log-render-crash Edge Function re-applies this function's exact
 *    content allowlist server-side, in front of a per-IP rate limit.
 */
export function recordRenderCrash(error: unknown, location?: string): void {
  const candidateLocation = location ?? getRenderCrashRouteGroup();
  const safeLocation =
    RELAY_ROUTE_GROUPS.has(candidateLocation) ||
    REPORTING_CRASH_LOCATIONS.has(candidateLocation) ||
    candidateLocation === "root"
      ? candidateLocation
      : "unknown";
  const safeErrorName =
    error instanceof Error && SAFE_ERROR_NAMES.has(error.name)
      ? error.name
      : "Error";

  const name = `error-boundary:render-crash:${safeLocation}`;
  const detail = { errorName: safeErrorName };

  void (async () => {
    let isAuthenticated = false;
    try {
      const { data } = await supabase.auth.getUser();
      isAuthenticated = Boolean(data.user?.id);
    } catch {
      /* unauthenticated or auth unavailable — treat as no session */
    }

    if (isAuthenticated) {
      logClientEvent({
        level: "error",
        source: "manual",
        name,
        message: "React render failed",
        detail,
        omitContext: true,
      });
      return;
    }

    try {
      await supabase.functions.invoke("relay-log-render-crash", {
        body: { name, detail },
      });
    } catch (err) {
      // Same never-throw contract as logClientEvent: a telemetry failure
      // must not surface beyond a dev-console warning.
      if (typeof console !== "undefined") {
        console.warn(
          "[client-events] failed to record anonymous render crash",
          err,
        );
      }
    }
  })();
}
